import {
    EnterpriseScopeCounterDiscriminator,
    useRepositoryMutation,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import {
    CommanderFormProps,
    SchemaFormFields,
    useDialogForm,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { createZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { createBooleanUpdateValue, createUpdateValue } from '@mews-commander/utils';
import { convertUseForFormValueToApplicableBillType } from '../../../utils';
import {
    creditNoteCounterValidationSchema,
    CreditNoteFormValues,
    getCreditNoteCounterFormDefinition,
    useDefaultValues,
} from './formMapping';

interface Params {
    counterId: string;
}

const EditCreditNoteCounterForm = ({ counterId, ...props }: Params & CommanderFormProps) => {
    const { mutateAsync } = useRepositoryMutation('creditNoteBillCounters', 'update', {
        invalidateQueries: { counters: ['getAll'] },
    });

    const { data: counter } = useRepositorySuspenseQuery(
        'counters',
        'getAll',
        {},
        {
            select: (result) => result.counters.find((it) => it.id === counterId),
            staleTime: 3000,
        }
    );

    const defaultValues = useDefaultValues(counter);

    const onSubmit = async (values: CreditNoteFormValues) => {
        const newBillType = convertUseForFormValueToApplicableBillType(values.useFor);
        const defaultBillType = convertUseForFormValueToApplicableBillType(defaultValues.useFor);

        if (!counter) return null;

        if (counter.data.discriminator !== EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter) return null;

        await mutateAsync({
            counterId: counter.id,
            prefix: createUpdateValue(defaultValues.prefix, values.prefix),
            displayCid: createBooleanUpdateValue(defaultValues.displayCID, values.displayCID),
            name: createUpdateValue(defaultValues.name, values.name),
            numberOfDigits: {
                value: values.numberOfDigits,
            },
            title: createUpdateValue(defaultValues.title, values.title),
            billTypeCode: createUpdateValue(defaultValues.billTypeCode, values.billTypeCode),
            isDefault: createBooleanUpdateValue(defaultValues.isDefault, values.isDefault),
            applicableBillType: createUpdateValue(defaultBillType, newBillType),
        });

        showSuccessAlert({
            title: lt('UpdateCounterSuccessMessage'),
        });
    };

    const { renderFormContent } = useHandledDialogFormControls<CreditNoteFormValues>({
        ...props,
        resolver: createZodFormResolver(creditNoteCounterValidationSchema),
        onSubmit,
        defaultValues,
    });

    return renderFormContent(
        <SchemaFormFields
            formDefinition={getCreditNoteCounterFormDefinition(true, defaultValues)}
            schema={creditNoteCounterValidationSchema}
        />
    );
};

export const useEditCreditNoteCounterDialog = ({ counterId }: Params) =>
    useDialogForm({
        title: lt('EditBillCounter'),
        renderForm: (props) => <EditCreditNoteCounterForm counterId={counterId} {...props} />,
    });
